import { v4 } from 'uuid';
import intl from 'react-intl-universal';
import { SnackbarProps as SnackbarNotificationProps } from 'Hooks/useSnackbarNotifications/useSnackbarNotifications';

export const generateBulkAssignNotifications = (
  successfulCount: number,
  failedCount: number,
  canUndo?: boolean
) => {
  const notifications: Map<string, SnackbarNotificationProps> = new Map();
  if (successfulCount > 0) {
    const successNotificationId = v4();
    notifications.set(successNotificationId, {
      id: successNotificationId,
      variant: 'success',
      title: intl.get('TASKS.BULK_ACTIONS.BULK_ACTIONS_SUCCESS', {
        num: successfulCount,
      }),
      ...(canUndo && {
        hasActionButton: true,
        actionButtonText: intl.get(
          'TASKS.BULK_ACTIONS.UNDO_BULK_ACTION_BUTTON'
        ),
        actionButtonPosition: 'right',
        autoHideDuration: 8000,
        hasCloseButton: true,
      }),
    });
  }
  if (failedCount > 0) {
    const errorNotificationId = v4();
    notifications.set(errorNotificationId, {
      id: errorNotificationId,
      variant: 'error',
      title: intl.get('TASKS.BULK_ACTIONS.BULK_ACTIONS_ERROR_TITLE'),
      message: intl.get('TASKS.BULK_ACTIONS.BULK_ACTIONS_ERROR_MESSAGE', {
        num: failedCount,
      }),
    });
  }
  return Object.fromEntries(notifications.entries());
};

export const generateRejectedBulkAssignNotifications = () => {
  const notifications: Map<string, SnackbarNotificationProps> = new Map();
  const errorNotificationId = v4();
  notifications.set(errorNotificationId, {
    id: errorNotificationId,
    variant: 'error',
    title: intl.get('TASKS.BULK_ACTIONS.BULK_ACTIONS_ERROR_TITLE'),
    message: intl.get('TASKS.BULK_ACTIONS.BULK_ACTIONS_ERROR_MESSAGE'),
  });
  return Object.fromEntries(notifications.entries());
};

export const generateBulkDeleteNotifications = (
  successfulCount: number,
  failedCount: number,
  operationId?: string
) => {
  const notifications: Map<string, SnackbarNotificationProps> = new Map();

  if (successfulCount > 0) {
    const successNotificationId = v4();
    notifications.set(successNotificationId, {
      id: successNotificationId,
      variant: 'success',
      title: intl.get('TASKS.BULK_ACTIONS.BULK_DELETE_SUCCESS', {
        num: successfulCount,
      }),
      ...(operationId && {
        hasActionButton: true,
        actionButtonText: intl.get(
          'TASKS.BULK_ACTIONS.UNDO_BULK_ACTION_BUTTON'
        ),
        actionButtonPosition: 'right',
        autoHideDuration: 8000,
        hasCloseButton: true,
      }),
    });
  }
  if (failedCount > 0) {
    const errorNotificationId = v4();
    notifications.set(errorNotificationId, {
      id: errorNotificationId,
      variant: 'error',
      title: intl.get('TASKS.BULK_ACTIONS.BULK_ACTIONS_ERROR_TITLE'),
      message: intl.get('TASKS.BULK_ACTIONS.BULK_DELETE_ERROR', {
        num: failedCount,
      }),
    });
  }
  return Object.fromEntries(notifications.entries());
};

export const generateRejectedBulkDeleteNotifications = () => {
  const notifications: Map<string, SnackbarNotificationProps> = new Map();
  const errorNotificationId = v4();
  notifications.set(errorNotificationId, {
    id: errorNotificationId,
    variant: 'error',
    title: intl.get('TASKS.BULK_ACTIONS.BULK_ACTIONS_ERROR_TITLE'),
    message: intl.get('TASKS.BULK_ACTIONS.BULK_DELETE_SYSTEM_TIMEOUT_ERROR'),
  });
  return Object.fromEntries(notifications.entries());
};

export const generateUndoBulkOperationNotifications = (failedCount: number) => {
  const notifications: Map<string, SnackbarNotificationProps> = new Map();
  if (failedCount > 0) {
    const errorNotificationId = v4();
    notifications.set(errorNotificationId, {
      id: errorNotificationId,
      variant: 'error',
      title: intl.get('TASKS.BULK_ACTIONS.NEW_CHANGES_TO_ITEMS_ERROR_TITLE'),
      message: intl.get(
        'TASKS.BULK_ACTIONS.NEW_CHANGES_TO_ITEMS_ERROR_MESSAGE'
      ),
    });
  } else {
    const successNotificationId = v4();
    notifications.set(successNotificationId, {
      id: successNotificationId,
      variant: 'success',
      title: intl.get('TASKS.BULK_ACTIONS.UNDO_BULK_ACTION_SUCCESS'),
    });
  }
  return Object.fromEntries(notifications.entries());
};

export const generateRejectedUndoBulkOperationNotifications = () => {
  const notifications: Map<string, SnackbarNotificationProps> = new Map();
  const errorNotificationId = v4();
  notifications.set(errorNotificationId, {
    id: errorNotificationId,
    variant: 'error',
    title: intl.get('TASKS.BULK_ACTIONS.BULK_ACTIONS_ERROR_TITLE'),
    message: intl.get('TASKS.BULK_ACTIONS.UNDO_BULK_ACTION_ERROR'),
  });
  return Object.fromEntries(notifications.entries());
};

export const generateBulkActionInProgressNotifications = () => {
  const notifications: Map<string, SnackbarNotificationProps> = new Map();
  const inProgressNotificationId = v4();
  notifications.set(inProgressNotificationId, {
    id: inProgressNotificationId,
    variant: 'information',
    title: intl.get('TASKS.BULK_ACTIONS.IN_PROGRESS_TITLE'),
    message: intl.get('TASKS.BULK_ACTIONS.IN_PROGRESS_MESSAGE'),
    autoHideDuration: 0,
  });
  return Object.fromEntries(notifications.entries());
};
